import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

function normalizeTabSlug(raw) {
  if (raw == null || typeof raw !== 'string') return '';
  try {
    return decodeURIComponent(raw).trim().toLowerCase();
  } catch {
    return String(raw).trim().toLowerCase();
  }
}

function withSearchAndHash(pathname, searchObj, hash) {
  const qs = searchObj.toString();
  return {
    pathname,
    search: qs ? `?${qs}` : '',
    hash: hash || '',
  };
}

/**
 * Syncs tab index with a URL search param (e.g. ?buchungen=anfragen), like Airbnb’s ?inbox_type=traveling.
 * Preserves other query keys on the same route.
 *
 * @param {Object} options
 * @param {string} options.paramName — query key (e.g. "buchungen").
 * @param {readonly string[]} options.tabIds — allowed values, in tab order.
 * @param {number} [options.defaultIndex=0]
 * @param {boolean} [options.clearParamForDefault=true] — omit param when the default tab is active.
 * @param {(detail: { index: number; id: string }) => void} [options.onTabChange]
 */
export function useSearchParamsTabs({
  paramName,
  tabIds,
  defaultIndex = 0,
  clearParamForDefault = true,
  onTabChange,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const onTabChangeRef = useRef(onTabChange);
  onTabChangeRef.current = onTabChange;

  const slugToIndex = useMemo(() => {
    const m = new Map();
    tabIds.forEach((id, i) => {
      const n = normalizeTabSlug(id);
      if (n) m.set(n, i);
    });
    return m;
  }, [tabIds]);

  const safeDefault = Math.min(Math.max(0, defaultIndex), Math.max(0, tabIds.length - 1));

  const resolveIndexFromParam = useCallback(
    (value) => {
      if (value == null || value === '') return safeDefault;
      const idx = slugToIndex.get(normalizeTabSlug(value));
      return idx !== undefined ? idx : null;
    },
    [slugToIndex, safeDefault]
  );

  const [activeIndex, setActiveIndex] = useState(() =>
    resolveIndexFromParam(
      typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get(paramName)
        : null
    ) ?? safeDefault
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get(paramName);
    const resolved = resolveIndexFromParam(raw);

    if (resolved === null) {
      setActiveIndex(safeDefault);
      params.delete(paramName);
      navigate(withSearchAndHash(location.pathname, params, location.hash), { replace: true });
      return;
    }
    setActiveIndex((prev) => (prev === resolved ? prev : resolved));
  }, [
    location.search,
    location.pathname,
    location.hash,
    paramName,
    navigate,
    resolveIndexFromParam,
    safeDefault,
  ]);

  const activeTabId = tabIds[activeIndex];

  useEffect(() => {
    if (activeTabId == null) return;
    onTabChangeRef.current?.({ index: activeIndex, id: activeTabId });
  }, [activeIndex, activeTabId]);

  const setTabIndex = useCallback(
    (index) => {
      if (index < 0 || index >= tabIds.length) return;
      const params = new URLSearchParams(location.search);
      if (clearParamForDefault && index === safeDefault) {
        params.delete(paramName);
      } else {
        params.set(paramName, tabIds[index]);
      }
      navigate(withSearchAndHash(location.pathname, params, location.hash), { replace: false });
      setActiveIndex(index);
    },
    [
      paramName,
      tabIds,
      location.pathname,
      location.search,
      location.hash,
      navigate,
      clearParamForDefault,
      safeDefault,
    ]
  );

  const activeId = tabIds[activeIndex] ?? tabIds[safeDefault] ?? '';

  return {
    activeIndex,
    activeId,
    setTabIndex,
    tabIds,
  };
}

export default useSearchParamsTabs;
